// src/pages/1_Symptoms.js
import React, { useState, useContext, useMemo, useRef, useEffect } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../../components/Layout';
import SSISymptomQuestionPanel from './components/SSISymptomQuestionPanel';
import Breadcrumb from '../../../components/Breadcrumb';
import SSISideNavbar from './components/SSISideNavbar';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { auth } from '../../../firebase';
import {
  isClinicianAssignedToStage,
  isSSILeadOverrideActive,
  isLeadClinician,
} from '../../../utils/clinicianPermissions';
import SSIResetBanner from '../../../components/SSIResetBanner';
import SSIOverrideBanner from '../../../components/SSIOverrideBanner';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import useSsiLockGuard from '../../../hooks/useSsiLockGuard';

export default function SemiStructuredInterviewSymptoms() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { patientDataModel, fetchPatientData, updatePatientFirestore } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // One-clinician SLaM: bounce back to the overview while the SSI is locked
  // (awaiting the end-of-CI decision, or the assessment has been ended).
  useSsiLockGuard({ patientDataModel, wf, patientId });
  
  const [informantsData, setInformantsData] = useState({});

  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    // Check if the current clinician is assigned to the SSI stage
    if (auth.currentUser && patientDataModel) {
      const hasPermission = isClinicianAssignedToStage(
        patientDataModel,
        auth.currentUser.uid,
        'SSI'
      );          
      setCanEdit(hasPermission);
    }
  }, [patientDataModel]);

  // Check if SSI reset is in progress (3 Borough / SLaM workflow)
  const ssiResetInfo = useMemo(() => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    if (!manageReports?.ssiResetRequired) {
      return { isResetRequired: false };
    }
    return {
      isResetRequired: true,
      resetRequestedByName: manageReports.ssiResetRequestedByName || 'Lead clinician',
      resetReason: manageReports.ssiResetReason || 'Conflict with Lead clinician findings',
      conflictType: manageReports.ssiResetConflictType || 'CI',
    };
  }, [patientDataModel]);

  // Lead takeover banner props — same shape as RD_SSI / Review so the
  // original SSI clinician sees a clear "you can view but not edit" message
  // when opening the live (post-takeover) proforma.
  const ssiOverrideActive = isSSILeadOverrideActive(patientDataModel);
  const leadOverride =
    patientDataModel?.dataModel?.['3_semiStructuredInterview']?.leadOverride;
  const snapshotPath = leadOverride?.archiveId
    ? `/patient/${patientId}/ssi-snapshot/${leadOverride.archiveId}`
    : null;
  const isCurrentUserLead = Boolean(
    auth.currentUser?.uid &&
      isLeadClinician(patientDataModel, auth.currentUser.uid)
  );

  // Add useEffect to fetch informant data
  useEffect(() => {
    const fetchInformantsData = async () => {
      if (!patientDataModel?.informants) return;
      
      const fetchedInformantsData = {};
      
      for (const informantId of Object.keys(patientDataModel.informants || {})) {
        try {
          const informantRef = doc(db, 'patients', informantId);
          const informantDoc = await getDoc(informantRef);
          
          if (informantDoc.exists()) {
            fetchedInformantsData[informantId] = informantDoc.data();
          }
        } catch (error) {
          console.error(`Error fetching informant data for ${informantId}:`, error);
        }
      }
      
      setInformantsData(fetchedInformantsData);
    };
    
    fetchInformantsData();
  }, [patientDataModel.informants]);

  const currentStageTitle = 'Semi-Structured Interview';
  const currentSectionTitle = "Symptoms";
  const currentSectionNumber = 1

  const SECTIONS_SSI = useMemo(() => Object.entries(patientDataModel['dataModel']['3_semiStructuredInterview']['sections']).map(([key, section]) => ({
    key,
    ...section
  })), [patientDataModel]);

  const questions = useMemo(() => {
    const section = SECTIONS_SSI.find(
      section => section.sectionTitle === currentSectionTitle
    );
    if (!section) {
      console.error(`Section with title "${currentSectionTitle}" not found.`);
      return [];
    }
    return Object.entries(section.questions || {}).map(([key, question]) => question);
  }, [SECTIONS_SSI, currentSectionTitle]);

  const [selections, setSelections] = useState(
    questions.map((question) => ({
      adulthood: question.symptomPresent_Adulthood !== "" ? question.symptomPresent_Adulthood : null,
      childhood: question.symptomPresent_Childhood !== "" ? question.symptomPresent_Childhood : null,
    }))
  );

  const [settings, setSettings] = useState(
    questions.map((question) => ({
      adulthood: question.presentInSettings_Adulthood || [],
    }))
  );

  // Add a new handler for settings changes
  const handleSettingsChange = (index, settingsArray) => {
    const newSettings = [...settings];
    newSettings[index].adulthood = settingsArray;
    setSettings(newSettings);

    // Update Firestore with the new settings
    updatePatientFirestore(patientId, {
      '3_semiStructuredInterview': {
        sections: {
          [currentSectionNumber]: {
            questions: {
              [index + 1]: {
                presentInSettings_Adulthood: settingsArray
              }
            }
          }
        }
      }
    });
  };

  const [notes, setNotes] = useState(
    questions.map((question) => ({
      adulthood: question.clinicianNotes_Adulthood || '',
      childhood: question.clinicianNotes_Childhood || '',
    }))
  );

  const [activeRecording, setActiveRecording] = useState(null);
  const [recognition, setRecognition] = useState(null);

  // Refs for text areas
  const textAreaRefs = useRef([]);

  useEffect(() => {
    // Ensure textAreaRefs is initialized with the correct structure
    if (questions.length > 0) {
      textAreaRefs.current = questions.map((_, i) => ({
        adulthood: textAreaRefs.current[i]?.adulthood || React.createRef(),
        childhood: textAreaRefs.current[i]?.childhood || React.createRef(),
      }));
    }
  }, [questions]);

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);

  // Add useEffect to handle navigation state from domains page
  useEffect(() => {
    if (location.state?.jumpToQuestion !== undefined) {
      const targetIndex = location.state.jumpToQuestion;
      if (targetIndex >= 0 && targetIndex < questions.length) {
        setActiveQuestionIndex(targetIndex);
      }
      // Clear the state to prevent issues on future navigations
      window.history.replaceState({}, '');
    }
  }, [location.state, questions.length]);
  
  // Handle selection of symptoms
  const handleSelection = (index, type, value) => {
    const newSelections = [...selections];
    newSelections[index][type] = newSelections[index][type] === value ? null : value;
    setSelections(newSelections);
  };

  // Handle note changes
  const handleNoteChange = (index, type, text) => {
    setNotes((prevNotes) => {
      const newNotes = [...prevNotes];
      newNotes[index][type.toLowerCase()] = text;
      return newNotes;
    });
  };

  // Handle speech recognition for notes
  const handleRecording = (index, type) => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }

    if (recognition) {
      recognition.stop();
      return;
    }

    const newRecognition = new window.webkitSpeechRecognition();
    newRecognition.continuous = true;
    newRecognition.interimResults = true;
    newRecognition.lang = 'en-GB';

    newRecognition.onresult = (event) => {
      let finalTranscript = notes[index][type];
      for (let i = event.resultIndex; i < event.results.length; i++) {
        let transcript = event.results[i][0].transcript.trim();
        if (event.results[i].isFinal) {
          transcript = ' ' + transcript.charAt(0).toUpperCase() + transcript.slice(1) + '.';
          finalTranscript += transcript;
        }
      }
      handleNoteChange(index, type, finalTranscript);
    };

    newRecognition.onend = () => {
      setActiveRecording(null);
      setRecognition(null);
  
      // Simulate focus and blur to trigger onBlur
      if (textAreaRefs.current[index] && textAreaRefs.current[index][type]?.current) {
        textAreaRefs.current[index][type].current.focus();
        setTimeout(() => {
          textAreaRefs.current[index][type].current.blur();
        }, 0); // Use a timeout to ensure the focus is set before blurring
      }
    };
  
    newRecognition.start();
    setRecognition(newRecognition);
    setActiveRecording({ index, type });
  
    // Focus the text area when recording starts
    if (textAreaRefs.current[index] && textAreaRefs.current[index][type]?.current) {
      textAreaRefs.current[index][type].current.focus();
    }
  };

  const jumpToQuestion = (index) => {
    setActiveQuestionIndex(index);
    // Scroll to top of the main content area when navigating between questions
    // The Layout component uses a flex container where the main content is in a scrollable div
    const mainContentArea = document.querySelector('.flex-1.overflow-y-auto');
    if (mainContentArea) {
      mainContentArea.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Fallback to window scroll if the container isn't found
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const breadcrumbTuples = [
    
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [currentSectionTitle]
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* SSI Reset / Lead takeover banners (3 Borough / SLaM workflow) */}
      <div className="px-8">
          <SSIResetBanner
            isVisible={ssiResetInfo.isResetRequired}
            resetRequestedByName={ssiResetInfo.resetRequestedByName}
            conflictType={ssiResetInfo.conflictType}
          />
          {ssiOverrideActive && (
            <div className="mt-3">
              <SSIOverrideBanner
                isVisible
                originalClinicianName={
                  leadOverride?.originalClinicianName ||
                  patientDataModel?.clinicians?.clinicianSSI?.clinicianName ||
                  'the original SSI clinician'
                }
                snapshotPath={snapshotPath}
                isCurrentUserLead={isCurrentUserLead}
              />
            </div>
          )}
      </div>
      <div className="flex">
        <SSISideNavbar
          questions={questions}
          activeQuestionIndex={activeQuestionIndex}
          selections={selections}
          jumpToQuestion={jumpToQuestion}
          currentSectionTitle={currentSectionTitle}
        />
        <div className="flex-grow">          
          <SSISymptomQuestionPanel
            currentStageTitle={currentStageTitle}
            currentSectionTitle={currentSectionTitle}
            currentSectionNumber={currentSectionNumber}
            questions={questions}
            question={questions[activeQuestionIndex]}
            index={activeQuestionIndex}
            selections={selections}
            notes={notes}
            settings={settings}
            activeRecording={activeRecording}
            handleSelection={handleSelection}
            handleNoteChange={handleNoteChange}
            handleRecording={handleRecording}
            handleSettingsChange={handleSettingsChange}
            jumpToQuestion={jumpToQuestion}
            patientDataModel={patientDataModel}
            fetchPatientData={fetchPatientData}
            updatePatientFirestore={updatePatientFirestore}
            patientId={patientId}
            navigate={navigate}
            textAreaRefs={textAreaRefs}
            informantsData={informantsData}
            canEdit={canEdit}
          />
        </div>
      </div>
    </Layout>
  );
}

